/**
 * SSML Readme のセクション切替タブ (よく使われる事例 / 構文辞典)。
 *
 * 既存のカテゴリタブ (`ReadmeCategoryNav`) と同じ
 * `role="tablist"` + `role="tab"` + `aria-selected` の実装パターンに揃える。
 *
 * ## 設計上の約束
 *
 * - **データは props で受ける** (`sections.ts` を import しない)。
 *   Phase2 で Readme が API 化されても、渡す配列の出所が変わるだけで済む (R14)。
 * - 選択状態は親 (`SsmlReadmePage`) が保持する。
 * - カテゴリタブとは **別の tablist** にする。事例 / 構文辞典の切替と、
 *   構文辞典内のカテゴリ切替は階層が違うため、同じ tablist に混ぜると
 *   支援技術上も「並列の選択肢」に見えてしまう。
 */
import type React from 'react';

import type { SsmlReadmeSection, SsmlReadmeSectionKey } from './types';

export interface ReadmeSectionNavProps {
  /** タブに並べるセクション (配列順がタブの並び順) */
  sections: readonly SsmlReadmeSection[];
  /** 選択中のセクションキー */
  activeSection: SsmlReadmeSectionKey;
  /** タブが押されたときの通知 (選択状態の保持は親の責務) */
  onSelectSection: (section: SsmlReadmeSectionKey) => void;
}

export function ReadmeSectionNav({
  sections,
  activeSection,
  onSelectSection,
}: ReadmeSectionNavProps): React.JSX.Element {
  return (
    <div
      className="ssml-readme-sections"
      role="tablist"
      aria-label="SSML Readme の表示切替"
      data-testid="ssml-readme-sections"
    >
      {sections.map((section) => {
        const isActive = section.section === activeSection;
        return (
          <button
            key={section.section}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`ssml-readme-section-panel-${section.section}`}
            id={`ssml-readme-section-tab-${section.section}`}
            className={`ssml-readme-section-tab${isActive ? ' is-active' : ''}`}
            data-testid={`ssml-readme-section-tab-${section.section}`}
            onClick={() => onSelectSection(section.section)}
          >
            {section.label}
          </button>
        );
      })}
    </div>
  );
}
